import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Upload, 
  Trash2, 
  Link as LinkIcon, 
  Copy, 
  Check, 
  HardDrive,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileArchive,
  FileSpreadsheet,
  Lock,
  Download,
  X,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_filelink-pro/artifacts/2x5fb85x_logo-avec-legende-et-Bakgroung-d%C3%A9taill%C3%A9.png";

// File type detection
const getFileIcon = (filename, type) => {
  const ext = filename?.toLowerCase().split('.').pop() || '';
  const mimeType = type?.toLowerCase() || '';
  
  if (['pdf'].includes(ext) || mimeType.includes('pdf')) {
    return <FileText className="w-5 h-5" />;
  }
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext) || mimeType.includes('document') || mimeType.includes('text')) {
    return <FileText className="w-5 h-5" />;
  }
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext) || mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <FileSpreadsheet className="w-5 h-5" />;
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext) || mimeType.includes('image')) {
    return <FileImage className="w-5 h-5" />;
  }
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv'].includes(ext) || mimeType.includes('video')) {
    return <FileVideo className="w-5 h-5" />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext) || mimeType.includes('archive') || mimeType.includes('zip')) {
    return <FileArchive className="w-5 h-5" />;
  }
  return <File className="w-5 h-5" />;
};

const getFileIconBg = (filename, type) => {
  const ext = filename?.toLowerCase().split('.').pop() || '';
  const mimeType = type?.toLowerCase() || '';
  
  if (['pdf'].includes(ext) || mimeType.includes('pdf')) return 'bg-[#C3171A]';
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) return 'bg-[#3B4394]';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'bg-[#166534]';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image')) return 'bg-purple-600';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext) || mimeType.includes('video')) return 'bg-pink-600';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'bg-amber-600';
  return 'bg-[#5C6673]';
};

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function Dashboard() {
  const [files, setFiles] = useState([]);
  const [storage, setStorage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkPassword, setLinkPassword] = useState("");
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fileInputRef = useRef(null);

  // Fetch files and storage info
  const fetchData = useCallback(async () => {
    try {
      const [filesRes, storageRes] = await Promise.all([
        axios.get(`${API}/files`),
        axios.get(`${API}/storage`)
      ]);
      // Ensure files is always an array
      const filesData = Array.isArray(filesRes.data) ? filesRes.data : [];
      setFiles(filesData);
      setStorage(storageRes.data || {});
    } catch (error) {
      console.error("Error fetching data:", error);
      setFiles([]);
      setStorage({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get resource type for Cloudinary
  const getResourceType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'raw';
  };

  // Upload file to Cloudinary
  const uploadFile = async (file) => {
    try {
      const resourceType = getResourceType(file);
      
      // Get signature from backend
      const sigRes = await axios.get(`${API}/cloudinary/signature`, {
        params: { resource_type: resourceType }
      });
      const sig = sigRes.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const cloudinaryRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );

      // Register file in our backend
      await axios.post(`${API}/files`, {
        ...cloudinaryRes.data,
        original_filename: file.name
      });

      return true;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const selectedFilesList = Array.from(e.target.files || []);
    if (selectedFilesList.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFilesList.length; i++) {
      try {
        await uploadFile(selectedFilesList[i]);
        successCount++;
      } catch (error) {
        errorCount++;
        toast.error(`Erreur: ${selectedFilesList[i].name}`);
      }
      setUploadProgress(Math.round(((i + 1) / selectedFilesList.length) * 100));
    }

    setUploading(false);
    setUploadProgress(0);
    
    if (successCount > 0) {
      toast.success(`${successCount} fichier(s) televerse(s) avec succes`);
      fetchData();
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;

    for (let i = 0; i < droppedFiles.length; i++) {
      try {
        await uploadFile(droppedFiles[i]);
        successCount++;
      } catch (error) {
        toast.error(`Erreur: ${droppedFiles[i].name}`);
      }
      setUploadProgress(Math.round(((i + 1) / droppedFiles.length) * 100));
    }

    setUploading(false);
    setUploadProgress(0);
    
    if (successCount > 0) {
      toast.success(`${successCount} fichier(s) televerse(s)`);
      fetchData();
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Select all files
  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => f.id));
    }
  };

  // Generate share link
  const generateLink = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Selectionnez au moins un fichier");
      return;
    }

    try {
      const res = await axios.post(`${API}/links`, {
        file_ids: selectedFiles,
        password: linkPassword || null
      });
      
      setGeneratedLink(res.data);
      toast.success("Lien genere avec succes");
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error("Erreur lors de la generation du lien");
    }
  };

  // Copy link to clipboard
  const copyLink = async () => {
    if (!generatedLink?.url) return;
    
    try {
      await navigator.clipboard.writeText(generatedLink.url);
      setCopied(true);
      toast.success("Lien copie dans le presse-papiers");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erreur lors de la copie");
    }
  };

  // Delete file
  const confirmDelete = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const deleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await axios.delete(`${API}/files/${fileToDelete.id}`);
      toast.success("Fichier supprime");
      setSelectedFiles(prev => prev.filter(id => id !== fileToDelete.id));
      fetchData();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  // Close link dialog
  const closeLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkPassword("");
    setGeneratedLink(null);
    setCopied(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B4394]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="app-header" data-testid="app-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="logo-container" data-testid="logo-container">
            <img src={LOGO_URL} alt="NERIUM" className="h-12" />
          </div>
          
          {/* Storage Indicator */}
          <div className="storage-widget" data-testid="storage-widget">
            <HardDrive className="w-5 h-5 text-[#3B4394]" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-[#0A0A0A]">
                {storage?.used_formatted || '0 B'} / {storage?.limit_formatted || '25 GB'}
              </span>
              <div className="w-32 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#3B4394] transition-all duration-300"
                  style={{ width: `${storage?.percentage || 0}%` }}
                />
              </div>
            </div>
            <span className="text-xs text-[#5C6673]">
              {storage?.files_count || 0} fichier(s)
            </span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Upload Zone */}
        <div 
          className={`nerium-upload-zone mb-8 ${dragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="file-upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            data-testid="file-input"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-[#3B4394]" />
              <div className="w-64">
                <Progress value={uploadProgress} className="h-2" />
              </div>
              <span className="text-[#5C6673]">Telechargement en cours... {uploadProgress}%</span>
            </div>
          ) : (
            <>
              <Upload className="w-12 h-12 text-[#3B4394] mb-4" />
              <h3 className="nerium-h3 mb-2">Deposez vos fichiers ici</h3>
              <p className="nerium-small">ou cliquez pour selectionner des fichiers</p>
              <p className="text-xs text-[#5C6673] mt-2">Tous types de fichiers acceptes - ZIP inclus</p>
            </>
          )}
        </div>

        {/* Actions Bar */}
        {files.length > 0 && (
          <div className="flex items-center justify-between mb-6 p-4 bg-[#F9F9FA] border border-[#E5E7EB] rounded-sm">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={selectedFiles.length === files.length && files.length > 0}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all-checkbox"
              />
              <span className="text-sm text-[#5C6673]">
                {selectedFiles.length} fichier(s) selectionne(s)
              </span>
            </div>
            
            <Button
              onClick={() => setLinkDialogOpen(true)}
              disabled={selectedFiles.length === 0}
              className="nerium-btn-primary"
              data-testid="generate-link-button"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Generer un lien
            </Button>
          </div>
        )}

        {/* Files List */}
        {files.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <File className="w-16 h-16 text-[#E5E7EB] mx-auto mb-4" />
            <h3 className="nerium-h3 text-[#5C6673]">Aucun fichier</h3>
            <p className="nerium-small mt-2">Commencez par telecharger des fichiers</p>
          </div>
        ) : (
          <div className="nerium-card" data-testid="files-list">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="nerium-table-header w-12"></th>
                  <th className="nerium-table-header">Fichier</th>
                  <th className="nerium-table-header">Taille</th>
                  <th className="nerium-table-header">Date</th>
                  <th className="nerium-table-header w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} className="nerium-table-row file-item" data-testid={`file-row-${file.id}`}>
                    <td className="nerium-table-cell">
                      <Checkbox
                        checked={selectedFiles.includes(file.id)}
                        onCheckedChange={() => toggleFileSelection(file.id)}
                        data-testid={`file-checkbox-${file.id}`}
                      />
                    </td>
                    <td className="nerium-table-cell">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center text-white ${getFileIconBg(file.original_filename, file.file_type)}`}>
                          {getFileIcon(file.original_filename, file.file_type)}
                        </div>
                        <div>
                          <p className="font-medium text-[#0A0A0A] truncate max-w-md" title={file.original_filename}>
                            {file.original_filename}
                          </p>
                          <p className="text-xs text-[#5C6673] uppercase">{file.file_type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="nerium-table-cell text-[#5C6673]">
                      {formatBytes(file.file_size)}
                    </td>
                    <td className="nerium-table-cell text-[#5C6673]">
                      {formatDate(file.uploaded_at)}
                    </td>
                    <td className="nerium-table-cell">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(file)}
                        className="text-[#C3171A] hover:text-[#8F1113] hover:bg-red-50"
                        data-testid={`delete-file-${file.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Generate Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={closeLinkDialog}>
        <DialogContent className="sm:max-w-md" data-testid="link-dialog">
          <DialogHeader>
            <DialogTitle className="nerium-h2">Generer un lien de partage</DialogTitle>
            <DialogDescription className="text-[#5C6673]">
              {selectedFiles.length} fichier(s) selectionne(s)
            </DialogDescription>
          </DialogHeader>
          
          {!generatedLink ? (
            <>
              <div className="space-y-4 py-4">
                <div>
                  <label className="nerium-label block mb-2">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Protection par mot de passe (optionnel)
                  </label>
                  <Input
                    type="password"
                    placeholder="Laissez vide pour un acces libre"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                    className="nerium-input"
                    data-testid="password-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={closeLinkDialog} className="nerium-btn-secondary">
                  Annuler
                </Button>
                <Button onClick={generateLink} className="nerium-btn-primary" data-testid="confirm-generate-link">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Generer
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="p-4 bg-[#F9F9FA] border border-[#E5E7EB] rounded-sm">
                  <p className="nerium-small mb-2">Votre lien de partage :</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm text-[#3B4394] break-all font-mono" data-testid="generated-link-url">
                      {generatedLink.url}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyLink}
                      className="shrink-0"
                      data-testid="copy-link-button"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-[#166534]" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                
                {generatedLink.has_password && (
                  <div className="flex items-center gap-2 text-sm text-[#5C6673]">
                    <Lock className="w-4 h-4" />
                    <span>Protege par mot de passe</span>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button onClick={closeLinkDialog} className="nerium-btn-primary">
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le fichier</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer "{fileToDelete?.original_filename}" ?
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="nerium-btn-secondary">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteFile} className="nerium-btn-destructive" data-testid="confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
